import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { inputClass } from "./FormField";
import { searchIcd10, type Icd10Code, type InvoiceAffection } from "../api/claimsApi";

type Props = {
  patientName: string;
  affections: InvoiceAffection[];
  editable: boolean;
  onAdd: (code: string) => void;
  onRemove: (code: string) => void;
};

/** Codes d'affection CIM-10 d'un patient : recherche par code ou libellé, plusieurs codes possibles. */
export function AffectionPicker({ patientName, affections, editable, onAdd, onRemove }: Props) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<Icd10Code[]>([]);

  useEffect(() => {
    if (term.trim().length < 2) {
      setResults([]);
      return;
    }
    const timer = window.setTimeout(() => {
      searchIcd10(term).then(setResults).catch(() => setResults([]));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [term]);

  const choose = (code: string) => {
    onAdd(code);
    setTerm("");
    setResults([]);
  };

  return (
    <div className="mb-2">
      <div className="flex flex-wrap gap-1">
        {affections.length === 0 ? <span className="text-xs text-amber-700">Aucun code d'affection</span> : null}
        {affections.map((affection) => (
          <span
            key={affection.code}
            title={affection.label}
            className="inline-flex max-w-full items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-foreground"
          >
            <strong className="font-mono">{affection.code}</strong>
            <span className="truncate">{affection.label}</span>
            {editable ? (
              <button
                type="button"
                aria-label={`Retirer ${affection.code}`}
                onClick={() => onRemove(affection.code)}
                className="text-muted hover:text-red-600"
              >
                <X size={12} />
              </button>
            ) : null}
          </span>
        ))}
      </div>
      {editable ? (
        <div className="relative mt-1">
          <input
            aria-label={`Ajouter un code d'affection pour ${patientName}`}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Code d'affection CIM-10 : B54, paludisme…"
            className={`${inputClass} h-8 text-xs`}
          />
          {results.length > 0 ? (
            <ul className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
              {results.map((result) => (
                <li key={result.code}>
                  <button
                    type="button"
                    onClick={() => choose(result.code)}
                    className="flex w-full gap-2 px-3 py-1.5 text-left text-xs hover:bg-accent/20"
                  >
                    <span className="font-mono font-semibold text-primary">{result.code}</span>
                    <span className="text-foreground">{result.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
